import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Center,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from "native-base";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { GiSteeringWheel } from "react-icons/gi";
import qs from "qs";
import styles from "./styles.module.css";
import colors from "../../../constants/colors";
import routes from "../../../routes";
import { StateContext, StatusContext } from "../../../contexts";
import API from "../../../api";
import shortid from "shortid";
import Seat from "../../../components/seat";
import Image from "../../../components/image";
import Map from "../../../components/map";
import { Marker } from "@react-google-maps/api";
import mapIcons from "../../../constants/mapIcons";

function TourSeatSelectionPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const sku = useMemo(() => searchParams.get("sku"), [searchParams]);
  const quotation = useMemo(
    () => searchParams.get("quotation") || null,
    [searchParams],
  );
  const status = useContext(StatusContext);
  const state = useContext(StateContext);
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [service, setService] = useState({});
  const [point, setPoint] = useState("");
  const [seats, setSeats] = useState([]);
  const [map, setMap] = useState();
  const [isSaving, setIsSaving] = useState(false);

  const getCart = useCallback(async () => {
    try {
      setIsLoading(true);
      const {
        data: { data },
      } = await API(state.jwt).get(`/carts/${sku}`);
      setItems(data.items);
      setSeats(data.items.map((item) => item.seat).filter(Boolean));
      setPoint(data?.location?.placeId || "");
      setService(data.service);
      setIsLoading(false);
    } catch (error) {
      status.updateErrorMessage(error);
      navigate(routes.tour.replace(":id", params.id));
    }
  }, [navigate, params.id, sku, state.jwt, status]);

  useEffect(() => {
    getCart();
  }, [getCart]);

  const points = useMemo(
    () =>
      service?.tour?.itinerary?.filter(
        (location) =>
          !location.isReturn &&
          ["origin", "boardingPoint"].includes(location.type),
      ) || [],
    [service],
  );

  const onUpdateItem = useCallback(
    (id, data) => {
      let _items = [...items];
      _items = _items.map((item) =>
        item.id === id ? { ...item, ...data } : item,
      );
      setItems(_items);
    },
    [items],
  );

  const capacity = useMemo(
    () => service?.quotation?.vehicleType?.capacity || 0,
    [service],
  );

  const onToggleSeat = useCallback(
    (seat) => {
      let _seats = [...seats];
      if (_seats.includes(seat)) {
        _seats = _seats.filter((_seat) => _seat !== seat);
      } else {
        if (_seats.length >= items.length) {
          status.updateInfoMessage(
            "Ya has seleccinado todos los asientos que tienes disponibles",
          );
          return;
        }
        _seats.push(seat);
      }
      setSeats(_seats);
    },
    [items.length, seats, status],
  );

  const origin = useMemo(
    () =>
      service?.tour?.itinerary?.find(
        (location) => !location.isReturn && location.type === "origin",
      ),
    [service],
  );

  const boardingPoints = useMemo(
    () =>
      service?.tour?.itinerary?.filter(
        (location) => !location.isReturn && location.type === "boardingPoint",
      ) || [],
    [service],
  );

  useEffect(() => {
    if (map) {
      const locations = [origin, ...boardingPoints].filter(
        (location) => location.placeId,
      );
      if (locations.length === 1) {
        const [location] = locations;
        map.panTo({
          lat: location.latitude,
          lng: location.longitude,
        });
      } else if (locations.length > 1) {
        const bounds = new window.google.maps.LatLngBounds();
        locations.forEach((location) =>
          bounds.extend(
            new window.google.maps.LatLng(
              location.latitude,
              location.longitude,
            ),
          ),
        );
        map.fitBounds(bounds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify([
      origin?.placeId,
      ...boardingPoints.map((boardingPoint) => boardingPoint.placeId),
    ]),
  ]);

  const onContinue = useCallback(async () => {
    try {
      if (quotation) {
        setIsSaving(true);
        const location = points.find((_point) => _point.placeId === point);
        delete location.id;
        await API(state.jwt).put(`/carts/${sku}`, {
          data: { items, location, quotation },
        });
        navigate(
          routes.experienceCreationPurchaseSummary.replace(":id", quotation),
        );
      } else {
        if (items.length !== seats.length) {
          status.updateWarningMessage("Seleccione todos los asientos");
          return;
        }
        setIsSaving(true);
        const location = points.find((_point) => _point.placeId === point);
        delete location.id;
        await API(state.jwt).put(`/carts/${sku}`, {
          data: {
            items: items.map((item, index) => ({
              ...item,
              seat: seats[index],
            })),
            location,
          },
        });
        navigate({
          pathname: routes.tourPurchaseSummary.replace(":id", params.id),
          search: qs.stringify({ sku }),
        });
      }
    } catch (error) {
      status.updateErrorMessage(error);
    }
    setIsSaving(false);
  }, [
    quotation,
    points,
    state.jwt,
    sku,
    items,
    navigate,
    point,
    seats,
    params.id,
    status,
  ]);

  if (isLoading) {
    return (
      <Center height="70vh">
        <Spinner size="lg" color={colors.primary} />
      </Center>
    );
  }

  return (
    <VStack>
      <Helmet>
        <title>Datos de contacto</title>
      </Helmet>
      <VStack paddingX={10} marginTop={10} space={10}>
        <HStack space={10} alignItems="flex-start">
          <VStack flex={1}>
            <form
              id="form"
              className={styles.form}
              onSubmit={(e) => {
                e.preventDefault();
                onContinue();
              }}>
              <Heading color={colors.secondary} fontSize="xl">
                Datos de contacto
              </Heading>
              <HStack space={5} marginTop={5}>
                <VStack space={1} flex={1}>
                  <Text>Correo electrónico</Text>
                  <Text>{state.user.email}</Text>
                </VStack>
                <VStack space={1} flex={1}>
                  <Text>Número de teléfono</Text>
                  <Text>{state.user.phone}</Text>
                </VStack>
              </HStack>
              <Heading color={colors.secondary} fontSize="xl" marginTop={10}>
                Datos del viajero
              </Heading>
              {items.map((item, index) => (
                <VStack key={item.id} marginTop={5} space={3}>
                  <Heading fontSize="lg">
                    Pasajero {index + 1} {item.name}
                  </Heading>
                  <HStack space={5}>
                    <label className={styles.input}>
                      <Text>Nombre</Text>
                      <input
                        type="text"
                        required
                        value={item.firstName}
                        onChange={(e) =>
                          onUpdateItem(item.id, { firstName: e.target.value })
                        }
                        disabled={isSaving}
                      />
                    </label>
                    <label className={styles.input}>
                      <Text>Apellido</Text>
                      <input
                        type="text"
                        required
                        value={item.lastName}
                        onChange={(e) =>
                          onUpdateItem(item.id, { lastName: e.target.value })
                        }
                        disabled={isSaving}
                      />
                    </label>
                  </HStack>
                </VStack>
              ))}
              <Heading
                color={colors.secondary}
                fontSize="xl"
                marginTop={10}
                marginBottom={5}>
                Detalles del tour
              </Heading>
              <label className={styles.input}>
                <Text bold>Elige tu punto de abordaje</Text>
                <select
                  value={point}
                  onChange={(e) => setPoint(e.target.value)}
                  required
                  disabled={isSaving}>
                  <option value="" disabled></option>
                  {points.map((point) => (
                    <option key={point.placeId} value={point.placeId}>
                      {point.name}
                    </option>
                  ))}
                </select>
              </label>
            </form>
          </VStack>
          {!service?.tour?.card4b && (
            <HStack flex={1} space={10}>
              <VStack space={5}>
                <Heading color={colors.secondary} fontSize="xl">
                  Elige tus asientos
                </Heading>
                <VStack
                  width={230}
                  paddingX={4}
                  paddingY={6}
                  borderColor={colors.secondary}
                  borderWidth={2}
                  borderRadius={10}
                  space={5}>
                  <GiSteeringWheel color={colors.gray} size={30} />
                  {Array.from(new Array(Math.ceil(capacity / 4))).map(
                    (_, i) => (
                      <HStack key={String(i)} justifyContent="space-between">
                        {Array.from(new Array(5)).map((_, j) => {
                          let position = i * 4 + j;
                          if (j <= 1) {
                            position += 1;
                          }
                          const isEmpty = position > capacity || j === 2;
                          return (
                            <Seat
                              key={
                                isEmpty ? shortid.generate() : String(position)
                              }
                              position={position}
                              isEmpty={isEmpty}
                              isBlocked={service?.bookings
                                ?.filter((booking) => booking.confirmed)
                                .some((booking) => booking.seat === position)}
                              isSelected={seats.includes(position)}
                              onSelect={onToggleSeat}
                            />
                          );
                        })}
                      </HStack>
                    ),
                  )}
                </VStack>
              </VStack>
              <VStack flex={1}>
                <HStack
                  backgroundColor={colors.secondary}
                  justifyContent="space-between"
                  paddingX={3}
                  paddingY={1}>
                  <VStack>
                    <Text bold color={colors.white}>
                      Folio
                    </Text>
                    <Text color={colors.white}>{service?.id}</Text>
                  </VStack>
                  <VStack>
                    <Text bold color={colors.white}>
                      Conductor
                    </Text>
                    <Text color={colors.white}>
                      {service?.driver?.id
                        ? `${service?.driver?.firstName} ${service?.driver?.lastName}`
                        : "SIN ASIGNAR"}
                    </Text>
                  </VStack>
                  <VStack>
                    <Text bold color={colors.white}>
                      Vehículo
                    </Text>
                    <Text color={colors.white}>
                      {service?.vehicle?.plate || "SIN ASIGNAR"}
                    </Text>
                  </VStack>
                </HStack>
                <Center>
                  <Image
                    image={service?.quotation?.vehicleType?.photos?.at(0)}
                    alt={`service #${service?.id}`}
                    width={220}
                    height={150}
                  />
                </Center>
                <HStack>
                  <VStack flex={1} alignItems="center">
                    <Text bold>Tipo de</Text>
                    <Text bold>unidad</Text>
                  </VStack>
                  <VStack flex={1} alignItems="center">
                    <Text bold>Asientos</Text>
                    <Text bold>disponibles</Text>
                  </VStack>
                  <VStack flex={1} alignItems="center">
                    <Text bold>Asientos</Text>
                    <Text bold>ocupados</Text>
                  </VStack>
                </HStack>
                <HStack>
                  <VStack flex={1} alignItems="center">
                    <Text>{service.quotation.vehicleType.name}</Text>
                  </VStack>
                  <VStack flex={1} alignItems="center">
                    <Text>
                      {service.quotation.vehicleType.capacity -
                        service.bookings.length}
                    </Text>
                  </VStack>
                  <VStack flex={1} alignItems="center">
                    <Text>{service.bookings.length}</Text>
                  </VStack>
                </HStack>
              </VStack>
            </HStack>
          )}
        </HStack>
        <Box height={400} width="full">
          <Map setRef={setMap}>
            {Boolean(origin.placeId) && (
              <Marker
                position={{ lat: origin.latitude, lng: origin.longitude }}
                icon={mapIcons.origin}
                title={origin.name}
              />
            )}
            {boardingPoints
              .filter((boardingPoint) => boardingPoint.placeId)
              .map((boardingPoint) => (
                <Marker
                  key={boardingPoint.placeId}
                  position={{
                    lat: boardingPoint.latitude,
                    lng: boardingPoint.longitude,
                  }}
                  icon={mapIcons.boardingPoint}
                  title={boardingPoint.name}
                />
              ))}
          </Map>
        </Box>
        <button className={styles.button} form="form" type="submit">
          <Box
            backgroundColor={colors.primary}
            paddingX={10}
            paddingY={3}
            borderRadius="full">
            <Text color={colors.white}>
              {quotation ? "Agregar" : "Reservar ahora"}
            </Text>
          </Box>
        </button>
      </VStack>
    </VStack>
  );
}

export default TourSeatSelectionPage;
